import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  AccessTime,
  CalendarToday,
  Notes,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";
import { bookingService } from "../services/bookingService";
import { dateUtils } from "../utils/dateUtils";

function BookingsList() {
  const [bookings, setBookings] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [cancellationOpen, setCancellationOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bookingsData, lessonTypesData] = await Promise.all([
        bookingService.getBookings(),
        bookingService.getLessonTypes(),
      ]);
      setBookings(bookingsData);
      setLessonTypes(lessonTypesData);
    } catch (error) {
      console.error("Error loading bookings:", error);
      setError("Errore nel caricamento delle prenotazioni");
    } finally {
      setLoading(false);
    }
  };

  const getLessonTypeName = (id) => {
    const lessonType = lessonTypes.find((type) => type.id === id);
    return lessonType ? lessonType.name : "Tipo sconosciuto";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "success";
      case "cancelled":
        return "error";
      case "completed":
        return "primary";
      default:
        return "warning";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "In attesa";
      case "confirmed":
        return "Confermata";
      case "cancelled":
        return "Cancellata";
      case "completed":
        return "Completata";
      default:
        return status;
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsOpen(true);
  };

  const handleConfirmBooking = async () => {
    if (!selectedBooking) return;

    try {
      setLoading(true);
      setError("");
      console.log("🔄 Inizio conferma prenotazione:", selectedBooking.id);

      // Update booking status to confirmed
      await bookingService.updateBooking(selectedBooking.id, {
        status: "confirmed",
        reminderSent: false,
      });
      console.log("✅ Stato prenotazione aggiornato a 'confirmed'");

      // Invia email di conferma al cliente
      try {
        console.log("📧 Tentativo invio email di conferma...");
        const sendConfirmation = httpsCallable(functions, "sendBookingConfirmation");
        const result = await sendConfirmation({ bookingId: selectedBooking.id });
        console.log("✅ Email di conferma inviata con successo:", result);
      } catch (emailError) {
        console.error("❌ Errore nell'invio dell'email di conferma:", emailError);
        console.error("Dettagli errore:", emailError.message, emailError.code);
        // Non blocchiamo il flusso se l'email fallisce
      }

      await loadData();
      setConfirmationOpen(false);
      setDetailsOpen(false);
      setSuccess("Prenotazione confermata con successo! Email inviata al cliente.");
      console.log("✅ Conferma prenotazione completata");
    } catch (error) {
      console.error("❌ Error confirming booking:", error);
      setError("Errore nella conferma della prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;

    try {
      setLoading(true);
      setError("");
      console.log("🔄 Inizio cancellazione prenotazione:", selectedBooking.id);

      // Update booking status
      await bookingService.updateBooking(selectedBooking.id, {
        status: "cancelled",
      });
      console.log("✅ Stato prenotazione aggiornato a 'cancelled'");

      // Invia email di cancellazione al cliente
      try {
        console.log("📧 Tentativo invio email di cancellazione...");
        const sendCancellation = httpsCallable(functions, "sendCancellationEmail");
        const result = await sendCancellation({ bookingId: selectedBooking.id });
        console.log("✅ Email di cancellazione inviata con successo:", result);
      } catch (emailError) {
        console.error("❌ Errore nell'invio dell'email di cancellazione:", emailError);
        console.error("Dettagli errore:", emailError.message, emailError.code);
        // Non blocchiamo il flusso se l'email fallisce
      }

      await loadData();
      setCancellationOpen(false);
      setDetailsOpen(false);
      setSuccess("Prenotazione cancellata con successo! Email inviata al cliente.");
      console.log("✅ Cancellazione prenotazione completata");
    } catch (error) {
      console.error("❌ Error cancelling booking:", error);
      setError("Errore nella cancellazione della prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      setLoading(true);
      await bookingService.updateBooking(bookingId, { status: "completed" });
      await loadData();
      setSuccess("Prenotazione marcata come completata!");
    } catch (error) {
      console.error("Error completing booking:", error);
      setError("Errore nell'aggiornamento della prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const filterBookingsByStatus = (status) => {
    return bookings.filter((booking) =>
      status === "all" || booking.status === status
    );
  };

  const tabOptions = [
    { value: "all", label: "Tutte", count: bookings.length },
    { value: "pending", label: "In attesa", count: filterBookingsByStatus("pending").length },
    { value: "confirmed", label: "Confermate", count: filterBookingsByStatus("confirmed").length },
    { value: "cancelled", label: "Cancellate", count: filterBookingsByStatus("cancelled").length },
  ];

  const getCurrentBookings = () => {
    const statusMap = ["all", "pending", "confirmed", "cancelled", "completed"];
    return filterBookingsByStatus(statusMap[activeTab]);
  };

  const columns = [
    {
      field: "date",
      headerName: "Data",
      width: 120,
      renderCell: (params) => {
        const date = params.value.toDate ? params.value.toDate() : new Date(params.value.seconds * 1000);
        return dateUtils.formatDate(date, "dd/MM/yyyy");
      },
    },
    {
      field: "startTime",
      headerName: "Orario",
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={<AccessTime />}
          label={params.value}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "userName",
      headerName: "Cliente",
      width: 150,
    },
    {
      field: "lessonTypeId",
      headerName: "Tipo Lezione",
      width: 250,
      renderCell: (params) => {
        const lessonType = lessonTypes.find((type) => type.id === params.value);
        return (
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <span>{getLessonTypeName(params.value)}</span>
            {lessonType?.isOnline && (
              <Chip label="Online" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
            {params.row.hasCarnet && (
              <Chip label="Pacchetto" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
          </Box>
        );
      },
    },
    {
      field: "userEmail",
      headerName: "Email",
      width: 200,
    },
    {
      field: "status",
      headerName: "Stato",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Azioni",
      width: 200,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
          >
            <VisibilityIcon />
          </IconButton>
          {params.row.status === "pending" && (
            <IconButton
              size="small"
              color="success"
              onClick={() => {
                setSelectedBooking(params.row);
                setConfirmationOpen(true);
              }}
            >
              <CheckCircleIcon />
            </IconButton>
          )}
          {(params.row.status === "pending" || params.row.status === "confirmed") && (
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedBooking(params.row);
                setCancellationOpen(true);
              }}
            >
              <CancelIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Gestione Prenotazioni
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Status Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabOptions.map((tab, index) => (
            <Tab
              key={tab.value}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {tab.label}
                  <Chip label={tab.count} size="small" />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Bookings DataGrid */}
      <Paper sx={{ height: 400 }}>
        <DataGrid
          rows={getCurrentBookings()}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
          sortModel={[{ field: 'date', sort: 'desc' }]}
        />
      </Paper>

      {/* Booking Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dettagli Prenotazione</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2} sx={{ pt: 2 }}>
              {/* Customer Info */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Informazioni Cliente
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PersonIcon />
                    <Typography>{selectedBooking.userName}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EmailIcon />
                    <Typography>{selectedBooking.userEmail}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon />
                    <Typography>{selectedBooking.userPhone}</Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Lesson Info */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Dettagli Lezione
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarToday />
                    <Typography>
                      {dateUtils.formatDate(
                        selectedBooking.date.toDate ? selectedBooking.date.toDate() : new Date(selectedBooking.date.seconds * 1000),
                        "EEEE, dd MMMM yyyy"
                      )}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AccessTime />
                    <Typography>
                      {selectedBooking.startTime} - {selectedBooking.endTime}
                    </Typography>
                  </Box>
                  <Typography mb={1}>
                    <strong>Tipo:</strong> {getLessonTypeName(selectedBooking.lessonTypeId)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <strong>Stato:</strong>
                    <Chip
                      label={getStatusLabel(selectedBooking.status)}
                      color={getStatusColor(selectedBooking.status)}
                      size="small"
                    />
                  </Box>
                  {selectedBooking.hasCarnet && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label="Acquistato tramite Pacchetto"
                        color="secondary"
                        size="small"
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Notes */}
              {(selectedBooking.notes || selectedBooking.adminNotes) && (
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Note
                    </Typography>
                    {selectedBooking.notes && (
                      <Box mb={2}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Notes />
                          <Typography variant="subtitle2">Note del cliente:</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {selectedBooking.notes}
                        </Typography>
                      </Box>
                    )}
                    {selectedBooking.adminNotes && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Note admin:
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {selectedBooking.adminNotes}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
      >
        <DialogTitle>Conferma Prenotazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler confermare questa prenotazione?
            Verrà inviata una email di conferma al cliente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)}>Annulla</Button>
          <Button
            onClick={handleConfirmBooking}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? "Confermando..." : "Conferma"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog
        open={cancellationOpen}
        onClose={() => setCancellationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancella Prenotazione</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Sei sicuro di voler cancellare questa prenotazione?
            Verrà inviata una email di notifica al cliente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancellationOpen(false)}>Annulla</Button>
          <Button
            onClick={handleCancelBooking}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? "Cancellando..." : "Cancella Prenotazione"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BookingsList;